import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './FileUpload.css';

const ALLOWED_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/heic',
  'image/heif',
];

const MAX_SIZE = 10 * 1024 * 1024; // 10MB

function uploadExtensionAllowed(fileName) {
  const ext = String(fileName || '')
    .split('.')
    .pop()
    ?.toLowerCase();
  return ['pdf', 'png', 'jpg', 'jpeg', 'heic'].includes(ext || '');
}

function FileUpload({ onUpload, onClose }) {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    if (!file) return t('upload.noFileSelected');
    const mimeOk = file.type && ALLOWED_TYPES.includes(file.type);
    if (!mimeOk && !uploadExtensionAllowed(file.name)) return t('upload.fileTypeNotSupported');
    if (file.size > MAX_SIZE) return t('upload.fileTooLarge');
    return null;
  };

  const handleFile = useCallback((file) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    onUpload(file);
  }, [onUpload, t]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  return (
    <div className="file-upload-overlay" onClick={onClose}>
      <div className="file-upload-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="file-upload-close"
          onClick={onClose}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <h3 className="file-upload-title">{t('upload.uploadFile')}</h3>
        <div
          className={`file-upload-dropzone ${isDragging ? 'dragging' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.heic,.HEIC,image/png,image/jpeg,image/heic,image/heif,application/pdf"
            onChange={handleFileSelect}
            hidden
          />
          <div className="file-upload-dropzone-content">
            <div className="file-upload-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <p className="file-upload-dropzone-text">{t('upload.dropFileHere')}</p>
            <p className="file-upload-dropzone-formats">{t('upload.supportedFormats')}</p>
          </div>
        </div>
        {error && (
          <div className="file-upload-error">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            {error}
          </div>
        )}
        <div className="file-upload-tips">
          <h4>{t('upload.tips')}:</h4>
          <ul>
            <li>{t('upload.tip1')}</li>
            <li>{t('upload.tip2')}</li>
            <li>{t('upload.tip3')}</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default FileUpload;
